import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, TextField, Button, Alert, Snackbar, CircularProgress, Divider,
  IconButton, Tabs, Tab, Tooltip, Paper, Chip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import RestoreIcon from '@mui/icons-material/Restore';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import SyncIcon from '@mui/icons-material/Sync';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import axios from 'axios';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';

/**
 * Master feature name mapping — mirrors allFeatureDefinitions in buildPublicPricingPayload
 * Maps feature keys from featurePricing to human-readable display names.
 */
const FEATURE_DISPLAY_NAMES = {
  coreAppFeatures: 'Core App Features',
  onlineStore: 'Online Store',
  advancedCart: 'Advanced Cart',
  scheduledDrops: 'Scheduled Drops',
  flashSales: 'Flash Sales',
  websiteBuilder: 'Website Builder',
  websiteAccess: 'Website Access',
  quickCreate: 'Quick Create',
  aiFeatures: 'AI Features',
  aiAnalytics: 'AI Analytics',
  voiceAssistant: 'Novo Voice (Voice AI)',
  voiceAssistantMcp: 'Novo Voice Desktop Connectors (Claude / ChatGPT MCP)',
  onlineBookingAppointments: 'Online Booking',
  calendarManagement: 'Calendar Management',
  shippingManagement: 'Shipping Management',
  paymentSystemIntegration: 'Payment Integration',
  advancedInvoicing: 'Advanced Invoicing',
  chatSystem: 'Chat System',
  emailManagement: 'Email Marketing',
  whatsappServiceIntegration: 'WhatsApp Integration',
  socialMediaIntegration: 'Social Media',
  multiLocationSupport: 'Multi-Branch',
  inventoryTransfers: 'Inventory Transfers',
  advancedReports: 'Advanced Reports',
  customFields: 'Custom Fields',
  consignmentOut: 'Consignment Out',
  returnsManagement: 'Returns Management',
  billsManagement: 'Bills Management',
  zapierIntegration: 'Zapier',
  apiAccess: 'API Access',
  aiFreeformSiteDesign: 'AI Freeform Site Design',
  taxLiabilityReport: 'Tax Reports',
  accountingExport: 'Accounting Export',
  tipManagement: 'Tip Management',
  shiftManagement: 'Shift Management',
  shiftCloseout: 'Shift Closeout',
  tipDistribution: 'Tip Distribution',
  postCloseAdjustments: 'Post-Close Adjustments',
  clientIntelligence: 'Client Intelligence & Recommendations'
};

const TabPanel = ({ children, value, index }) => (
  <div hidden={value !== index}>{value === index && <Box sx={{ pt: 2 }}>{children}</Box>}</div>
);

const defaultFeatures = {
  free: ['Dashboard & Analytics', 'Basic Inventory Management', 'Client & Supplier Management', 'Basic Invoicing', 'Up to 20 Products/Services', 'Single Branch', 'Email Support'],
  standard: ['Everything in Free', 'Unlimited Products/Services', 'Online Store', 'Website Builder', 'AI Ad Generation', 'Online Appointments', 'Advanced Invoicing', 'Shipping Management', 'Priority Support'],
  professional: ['Everything in Standard', 'Multiple Branches', 'Unlimited Products/Services', 'Online Store', 'Website Builder', 'AI Ad Generation', 'Online Appointments', 'Advanced Invoicing', 'Shipping Management', 'Priority Support'],
  enterprise: ['Everything in Professional', 'Multiple Branches', 'Multiple Companies', 'Inventory Transfers', 'Advanced User Roles', 'API Access', 'Zapier Integration', 'Custom Fields', 'Dedicated Support']
};

/**
 * Reorders an array by moving the item at startIndex to endIndex.
 */
function reorderList(list, startIndex, endIndex) {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
}

/**
 * Individual draggable feature row with inline editing.
 */
function FeatureRow({ feature, index, tier, saving, onRemove, onEditSave, isAutoPopulated, hasWarning }) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(feature);

  // Sync editValue if the feature prop changes (e.g. after reorder)
  useEffect(() => {
    if (!editing) {
      setEditValue(feature);
    }
  }, [feature, editing]);

  const handleStartEdit = () => {
    setEditValue(feature);
    setEditing(true);
  };

  const handleCancelEdit = () => {
    setEditValue(feature);
    setEditing(false);
  };

  const handleSaveEdit = () => {
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== feature) {
      onEditSave(tier, index, trimmed);
    }
    setEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSaveEdit();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  return (
    <Draggable draggableId={`${tier}-feature-${index}`} index={index}>
      {(provided, snapshot) => (
        <Paper
          ref={provided.innerRef}
          {...provided.draggableProps}
          elevation={snapshot.isDragging ? 4 : 0}
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 1.5,
            py: editing ? 0.75 : 1,
            mb: 0.5,
            borderRadius: 1,
            border: '1px solid',
            borderColor: snapshot.isDragging ? 'primary.main' : 'divider',
            bgcolor: snapshot.isDragging ? 'action.hover' : 'background.paper',
            transition: snapshot.isDragging ? 'none' : 'background-color 0.2s, border-color 0.2s',
            '&:hover': {
              bgcolor: 'action.hover'
            }
          }}
        >
          {/* Drag handle */}
          <Box
            {...provided.dragHandleProps}
            sx={{
              display: 'flex',
              alignItems: 'center',
              mr: 1,
              color: 'text.secondary',
              cursor: 'grab',
              '&:active': { cursor: 'grabbing' }
            }}
          >
            <DragIndicatorIcon fontSize="small" />
          </Box>

          {/* Feature number chip */}
          <Chip
            label={index + 1}
            size="small"
            sx={{
              mr: 1.5,
              minWidth: 28,
              height: 24,
              fontSize: '0.75rem',
              fontWeight: 600,
              bgcolor: 'primary.main',
              color: 'primary.contrastText'
            }}
          />

          {/* Feature text or edit field */}
          {editing ? (
            <TextField
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={handleKeyDown}
              size="small"
              fullWidth
              autoFocus
              disabled={saving}
              sx={{ mr: 1 }}
              InputProps={{
                sx: { fontSize: '0.875rem' }
              }}
            />
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0, gap: 0.75 }}>
              <Typography
                variant="body2"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {feature}
              </Typography>
              {isAutoPopulated && (
                <Tooltip title="Auto-populated from Feature Pricing" arrow>
                  <Chip
                    icon={<AutoFixHighIcon sx={{ fontSize: '0.7rem' }} />}
                    label="Auto"
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.65rem',
                      fontWeight: 600,
                      bgcolor: 'info.light',
                      color: 'info.contrastText',
                      flexShrink: 0,
                      '& .MuiChip-icon': { ml: 0.5 }
                    }}
                  />
                </Tooltip>
              )}
              {hasWarning && (
                <Tooltip title="This feature is DISABLED in Feature Pricing but still shown in Paywall Display" arrow>
                  <WarningAmberIcon sx={{ fontSize: '1rem', color: 'warning.main', flexShrink: 0 }} />
                </Tooltip>
              )}
            </Box>
          )}

          {/* Action buttons */}
          <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto', gap: 0.25, flexShrink: 0 }}>
            {editing ? (
              <>
                <Tooltip title="Save" arrow>
                  <IconButton
                    size="small"
                    onClick={handleSaveEdit}
                    disabled={saving || !editValue.trim()}
                    color="success"
                  >
                    <CheckIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Cancel" arrow>
                  <IconButton size="small" onClick={handleCancelEdit} disabled={saving}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            ) : (
              <>
                <Tooltip title="Edit feature" arrow>
                  <IconButton size="small" onClick={handleStartEdit} disabled={saving}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete feature" arrow>
                  <IconButton
                    size="small"
                    onClick={() => onRemove(tier, index)}
                    disabled={saving}
                    color="error"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Box>
        </Paper>
      )}
    </Draggable>
  );
}

export default function PaywallDisplayContent() {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [features, setFeatures] = useState({ free: [], standard: [], professional: [], enterprise: [] });
  const [newFeature, setNewFeature] = useState({ free: '', standard: '', professional: '', enterprise: '' });

  // Feature pricing data from the "Feature Pricing" section
  const [featurePricingData, setFeaturePricingData] = useState({});
  // Tier limits (maxProducts, maxAppointments, etc.)
  const [tierLimitsData, setTierLimitsData] = useState({});
  // Storage/bandwidth free allowances per tier
  const [storageBandwidthData, setStorageBandwidthData] = useState(null);

  useEffect(() => { fetchFeatures(); }, []);

  const fetchFeatures = async () => {
    try {
      setLoading(true);

      // Fetch all data in parallel: display features, feature pricing, and storage/bandwidth
      const [subscriptionRes, featurePricingRes] = await Promise.all([
        axios.get('/api/subscription/config'),
        axios.get('/api/config/feature-pricing')
      ]);

      // Set display features
      let loadedFeatures = { free: [], standard: [], professional: [], enterprise: [] };
      if (subscriptionRes.data.success) {
        const { tiers } = subscriptionRes.data;
        loadedFeatures = {
          free: tiers.free?.features || [],
          standard: tiers.standard?.features || [],
          professional: tiers.professional?.features || [],
          enterprise: tiers.enterprise?.features || []
        };
        setFeatures(loadedFeatures);
      }

      // Set feature pricing data and tier limits
      let loadedTierLimits = {};
      if (featurePricingRes.data.success) {
        setFeaturePricingData(featurePricingRes.data.featurePricing || {});
        const tp = featurePricingRes.data.tierPricing || {};
        loadedTierLimits = {
          free: tp.free?.features || {},
          standard: tp.standard?.features || {},
          professional: tp.professional?.features || {},
          enterprise: tp.enterprise?.features || {}
        };
        setTierLimitsData(loadedTierLimits);
      }

      // Fetch storage/bandwidth allowances (optional — don't block on failure)
      try {
        const pricingRes = await axios.get('/api/pricing/public');
        if (pricingRes.data.success) {
          setStorageBandwidthData({
            storage: pricingRes.data.pricing?.storage?.freeAllowances || null,
            bandwidth: pricingRes.data.pricing?.bandwidth?.freeAllowances || null
          });
        }
      } catch {
        // Storage/bandwidth data is optional — ignore errors
      }

      // Auto-sync "Multi-Branch Support" based on Features & Limits configuration
      // Uses featurePricing.multiLocationSupport.availableInTiers as the source of truth
      const loadedFeaturePricing = featurePricingRes.data.featurePricing || {};
      const multiLocationConfig = loadedFeaturePricing.multiLocationSupport;
      const multiStoreKeywords = ['multi-store', 'multi store', 'multi-branch', 'multi branch', 'multiple stores', 'multiple branches', 'unlimited branches', 'multi-location'];
      const hasMultiStoreText = (featureList) =>
        featureList.some(f => multiStoreKeywords.some(kw => f.toLowerCase().includes(kw)));

      const tiersToAdd = [];
      const tiersToRemove = [];
      for (const tierName of ['free', 'standard', 'professional', 'enterprise']) {
        const tierFeatures = loadedFeatures[tierName] || [];
        // Check Features & Limits configuration for whether this tier has multi-location enabled
        const multiLocationEnabledForTier = multiLocationConfig?.enabled === true &&
          Array.isArray(multiLocationConfig?.availableInTiers) &&
          multiLocationConfig.availableInTiers.includes(tierName);

        if (multiLocationEnabledForTier && tierFeatures.length > 0 && !hasMultiStoreText(tierFeatures)) {
          tiersToAdd.push(tierName);
        } else if (!multiLocationEnabledForTier && tierFeatures.length > 0 && hasMultiStoreText(tierFeatures)) {
          // Remove Multi-Branch text from tiers where it's no longer enabled
          tiersToRemove.push(tierName);
        }
      }

      if (tiersToAdd.length > 0 || tiersToRemove.length > 0) {
        const updatedFeatures = { ...loadedFeatures };

        for (const tierName of tiersToAdd) {
          updatedFeatures[tierName] = ['Multi-Branch Support', ...updatedFeatures[tierName]];
          try {
            await axios.put('/api/subscription/tier-features', { tier: tierName, features: updatedFeatures[tierName] });
          } catch {
            // Non-critical — display update still applies
          }
        }

        for (const tierName of tiersToRemove) {
          updatedFeatures[tierName] = updatedFeatures[tierName].filter(
            f => !multiStoreKeywords.some(kw => f.toLowerCase().includes(kw))
          );
          try {
            await axios.put('/api/subscription/tier-features', { tier: tierName, features: updatedFeatures[tierName] });
          } catch {
            // Non-critical — display update still applies
          }
        }

        setFeatures(updatedFeatures);
      }

    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to load features', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const saveFeatures = async (tier, tierFeatures) => {
    try {
      setSaving(true);
      const response = await axios.put('/api/subscription/tier-features', { tier, features: tierFeatures });
      if (response.data.success) {
        setSnackbar({ open: true, message: `${tier.charAt(0).toUpperCase() + tier.slice(1)} tier features updated`, severity: 'success' });
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to save features', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const addFeature = (tier) => {
    if (newFeature[tier].trim()) {
      const updated = [...features[tier], newFeature[tier].trim()];
      setFeatures(prev => ({ ...prev, [tier]: updated }));
      setNewFeature(prev => ({ ...prev, [tier]: '' }));
      saveFeatures(tier, updated);
    }
  };

  const removeFeature = useCallback((tier, index) => {
    setFeatures(prev => {
      const updated = prev[tier].filter((_, i) => i !== index);
      saveFeatures(tier, updated);
      return { ...prev, [tier]: updated };
    });
  }, []);

  const editFeature = useCallback((tier, index, newValue) => {
    setFeatures(prev => {
      const updated = [...prev[tier]];
      updated[index] = newValue;
      saveFeatures(tier, updated);
      return { ...prev, [tier]: updated };
    });
  }, []);

  const restoreDefaults = async (tier) => {
    setFeatures(prev => ({ ...prev, [tier]: defaultFeatures[tier] }));
    await saveFeatures(tier, defaultFeatures[tier]);
    setSnackbar({ open: true, message: `${tier.charAt(0).toUpperCase() + tier.slice(1)} tier restored to defaults`, severity: 'success' });
  };

  /**
   * Compute auto-generated feature display strings from Feature Pricing data for a tier.
   * Returns an array of human-readable marketing strings derived from:
   *   1. Enabled features (mapped via FEATURE_DISPLAY_NAMES)
   *   2. Tier limits (maxProducts, maxAppointments, etc.)
   *   3. Storage/bandwidth free allowances
   */
  const computeAutoFeatures = useCallback((tier) => {
    const autoFeatures = [];

    // 1. Enabled features from Feature Pricing
    for (const [featureKey, featureConfig] of Object.entries(featurePricingData)) {
      if (featureKey.startsWith('_')) continue;
      // Admin kill switch — owners hear the named plan from iOS getRequiredTier,
      // not from this customer-facing paywall bullet list.
      if (featureKey === 'voiceFeatureSetup') continue;
      if (featureConfig?.enabled && featureConfig?.availableInTiers?.includes(tier)) {
        const displayName = FEATURE_DISPLAY_NAMES[featureKey] || featureKey;
        autoFeatures.push(displayName);
      }
    }

    // 2. Tier limits
    const limits = tierLimitsData[tier] || {};
    if (limits.maxProducts !== undefined && limits.maxProducts !== null) {
      autoFeatures.push(`Up to ${limits.maxProducts} Products`);
    } else if (limits.maxProducts === null) {
      autoFeatures.push('Unlimited Products');
    }
    if (limits.maxAppointments !== undefined && limits.maxAppointments !== null) {
      autoFeatures.push(`Up to ${limits.maxAppointments} Appointments`);
    } else if (limits.maxAppointments === null) {
      autoFeatures.push('Unlimited Appointments');
    }
    if (limits.maxWebsiteProducts !== undefined && limits.maxWebsiteProducts !== null) {
      autoFeatures.push(`Up to ${limits.maxWebsiteProducts} Website Products`);
    } else if (limits.maxWebsiteProducts === null) {
      autoFeatures.push('Unlimited Website Products');
    }
    if (limits.maxCompanies !== undefined && limits.maxCompanies !== null && limits.maxCompanies > 1) {
      autoFeatures.push(`Up to ${limits.maxCompanies} Companies`);
    }

    // Only add "Multi-Branch Support" if BOTH conditions are met:
    // 1. Tier limit allows multiple stores (maxStores > 1)
    // 2. Multi-location feature is enabled for this tier in Features & Limits
    const multiLocationConfig = featurePricingData.multiLocationSupport;
    const isMultiLocationEnabled = multiLocationConfig?.enabled === true &&
      Array.isArray(multiLocationConfig?.availableInTiers) &&
      multiLocationConfig.availableInTiers.includes(tier);

    if (limits.maxStores !== undefined && limits.maxStores !== null && limits.maxStores > 1 && isMultiLocationEnabled) {
      autoFeatures.push('Multi-Branch Support');
    }
    if (limits.maxUsers === null) {
      autoFeatures.push('Unlimited Users');
    } else if (limits.maxUsers !== undefined && limits.maxUsers !== null) {
      autoFeatures.push(`Up to ${limits.maxUsers} Users`);
    }

    // 3. Storage/bandwidth free allowances
    if (storageBandwidthData) {
      const storageGB = storageBandwidthData.storage?.[tier];
      if (storageGB !== undefined && storageGB !== null) {
        autoFeatures.push(storageGB >= 1 ? `${storageGB} GB Storage` : `${Math.round(storageGB * 1000)} MB Storage`);
      }
      const bandwidthGB = storageBandwidthData.bandwidth?.[tier];
      if (bandwidthGB !== undefined && bandwidthGB !== null) {
        autoFeatures.push(bandwidthGB >= 1 ? `${bandwidthGB} GB Bandwidth` : `${Math.round(bandwidthGB * 1000)} MB Bandwidth`);
      }
    }

    return autoFeatures;
  }, [featurePricingData, tierLimitsData, storageBandwidthData]);

  /**
   * Sync display features from Feature Pricing for a specific tier.
   * Merges auto-generated features with existing manual features (preserving manual ones).
   */
  const syncFromFeaturePricing = async (tier) => {
    try {
      setSyncing(true);
      const autoFeatures = computeAutoFeatures(tier);
      const existingFeatures = features[tier] || [];

      // Build a set of auto-generated feature texts (case-insensitive) for dedup
      const autoSet = new Set(autoFeatures.map(f => f.toLowerCase()));

      // Keep existing manual features that are NOT duplicates of auto-generated ones
      const manualFeatures = existingFeatures.filter(f => !autoSet.has(f.toLowerCase()));

      // Merged list: auto-generated first, then manual
      const merged = [...autoFeatures, ...manualFeatures];

      setFeatures(prev => ({ ...prev, [tier]: merged }));
      await saveFeatures(tier, merged);
      setSnackbar({
        open: true,
        message: `${tier.charAt(0).toUpperCase() + tier.slice(1)} tier synced: ${autoFeatures.length} auto features + ${manualFeatures.length} manual features`,
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to sync features', severity: 'error' });
    } finally {
      setSyncing(false);
    }
  };

  const handleDragEnd = useCallback((result) => {
    const { source, destination } = result;

    // Dropped outside the list or in the same position
    if (!destination || (source.index === destination.index && source.droppableId === destination.droppableId)) {
      return;
    }

    // Extract tier name from droppableId (e.g. "droppable-free" => "free")
    const tier = source.droppableId.replace('droppable-', '');
    const reordered = reorderList(features[tier], source.index, destination.index);

    // Get the feature that was moved
    const movedFeature = reordered[destination.index];
    const movedFeatureLower = movedFeature.toLowerCase();
    const targetPosition = destination.index;

    // Update all tiers: reorder the current tier AND sync the same feature position in other tiers
    const updatedFeatures = { ...features };
    const allTiers = ['free', 'standard', 'professional', 'enterprise'];

    // First, update the current tier
    updatedFeatures[tier] = reordered;

    // Then, find and reorder the same feature in all other tiers
    for (const otherTier of allTiers) {
      if (otherTier === tier) continue; // Skip the tier we just reordered

      const otherTierFeatures = [...(features[otherTier] || [])];

      // Find the feature in this tier (case-insensitive)
      const featureIndex = otherTierFeatures.findIndex(f => f.toLowerCase() === movedFeatureLower);

      if (featureIndex !== -1) {
        // Feature exists in this tier - move it to the same position
        const [removed] = otherTierFeatures.splice(featureIndex, 1);

        // Insert at target position, but don't exceed array length
        const insertPosition = Math.min(targetPosition, otherTierFeatures.length);
        otherTierFeatures.splice(insertPosition, 0, removed);

        updatedFeatures[otherTier] = otherTierFeatures;
      }
    }

    // Update state with all changes
    setFeatures(updatedFeatures);

    // Save all affected tiers
    for (const tierToSave of allTiers) {
      if (updatedFeatures[tierToSave] !== features[tierToSave]) {
        saveFeatures(tierToSave, updatedFeatures[tierToSave]);
      }
    }
  }, [features]);

  const tiers = ['free', 'standard', 'professional', 'enterprise'];

  const renderFeatureList = (tier) => (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h6">
            {tier.charAt(0).toUpperCase() + tier.slice(1)} Tier Features
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            {features[tier].length} feature{features[tier].length !== 1 ? 's' : ''} — drag to reorder, click edit to modify
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Auto-populate from enabled features in Feature Pricing, tier limits, and storage/bandwidth allowances. Existing manual features are preserved." arrow>
            <Button
              variant="contained"
              size="small"
              startIcon={syncing ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
              onClick={() => syncFromFeaturePricing(tier)}
              disabled={saving || syncing || Object.keys(featurePricingData).length === 0}
              color="primary"
            >
              Sync from Feature Pricing
            </Button>
          </Tooltip>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RestoreIcon />}
            onClick={() => restoreDefaults(tier)}
            disabled={saving || syncing}
          >
            Restore Defaults
          </Button>
        </Box>
      </Box>

      {/* Drag and drop feature list */}
      {(() => {
        // Compute auto-populated feature set for this tier (case-insensitive)
        const autoFeatures = computeAutoFeatures(tier);
        const autoSet = new Set(autoFeatures.map(f => f.toLowerCase()));

        // Compute warning set: feature display names that match a DISABLED feature in Feature Pricing
        const disabledDisplayNames = new Set();
        for (const [featureKey, featureConfig] of Object.entries(featurePricingData)) {
          if (featureKey.startsWith('_')) continue;
          if (!featureConfig?.enabled || !featureConfig?.availableInTiers?.includes(tier)) {
            const displayName = FEATURE_DISPLAY_NAMES[featureKey];
            if (displayName) disabledDisplayNames.add(displayName.toLowerCase());
          }
        }

        return (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId={`droppable-${tier}`}>
              {(provided, snapshot) => (
                <Box
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  sx={{
                    minHeight: 48,
                    p: 1,
                    borderRadius: 1,
                    bgcolor: snapshot.isDraggingOver ? 'action.selected' : 'grey.50',
                    transition: 'background-color 0.2s'
                  }}
                >
                  {features[tier].length === 0 ? (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ textAlign: 'center', py: 3 }}
                    >
                      No features configured. Add features below or restore defaults.
                    </Typography>
                  ) : (
                    features[tier].map((feature, index) => (
                      <FeatureRow
                        key={`${tier}-${index}-${feature}`}
                        feature={feature}
                        index={index}
                        tier={tier}
                        saving={saving}
                        onRemove={removeFeature}
                        onEditSave={editFeature}
                        isAutoPopulated={autoSet.has(feature.toLowerCase())}
                        hasWarning={disabledDisplayNames.has(feature.toLowerCase())}
                      />
                    ))
                  )}
                  {provided.placeholder}
                </Box>
              )}
            </Droppable>
          </DragDropContext>
        );
      })()}

      {/* Add new feature */}
      <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
        <TextField
          fullWidth
          label="Add New Feature"
          value={newFeature[tier]}
          onChange={(e) => setNewFeature(prev => ({ ...prev, [tier]: e.target.value }))}
          onKeyDown={(e) => e.key === 'Enter' && addFeature(tier)}
          disabled={saving}
          size="small"
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => addFeature(tier)}
          disabled={saving || !newFeature[tier].trim()}
          sx={{ whiteSpace: 'nowrap' }}
        >
          Add
        </Button>
      </Box>
    </Box>
  );

  if (loading) return <Box sx={{ textAlign: 'center', p: 4 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 3 }}>
        <strong>Display Only:</strong> This controls marketing text shown on the paywall screen. 
        It does NOT enable/disable features - use "Features &amp; Limits" tab for that.
      </Alert>

      <Divider sx={{ my: 2 }} />

      <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 2 }}>
        {tiers.map((tier) => (
          <Tab key={tier} label={`${tier.charAt(0).toUpperCase() + tier.slice(1)} Tier`} />
        ))}
      </Tabs>

      {tiers.map((tier, index) => (
        <TabPanel key={tier} value={tabValue} index={index}>
          {renderFeatureList(tier)}
        </TabPanel>
      ))}

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
